// libs
import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

// hooks
import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'

// constants
import { VERBATIM_PLACEHOLDER_HEIGHT } from '@aytm/app/stats/page/question-views/oe/lib/constants'

// helpers
import copyToClipboard from '@aytm/lib/common/copy-to-clipboard'
import { decodeText } from '@aytm/app/stats/lib/util/helpers'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// components
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'

// styles
import style from '@aytm/app/stats/page/question-views/oe/styles/VerbatimView.module.scss'

// constants
const COLUMN_GAP = 10
const COLUMN_PADDING = 30
const DEFAULT_WIDTH = 660

const presetSortFunctions = {
  longest: {
    items: (a, b) => b.quote.length - a.quote.length,
    columns: (a, b) => Math.max(...b.items.map(v => v.quote.length)) - Math.max(...a.items.map(v => v.quote.length))
  },
  shortest: {
    items: (a, b) => a.quote.length - b.quote.length,
    columns: (a, b) => Math.min(...a.items.map(v => v.quote.length)) - Math.min(...b.items.map(v => v.quote.length))
  },
  median: {
    items: (a, b) => a.index - b.index,
    columns: (a, b) => Math.max(...a.items.map(v => v.index)) - Math.max(...b.items.map(v => v.index))
  }
}

const calcHeight = items => items.reduce((acc, { height }) => acc + height, 0) + COLUMN_GAP * (items.length - 1)

const calcColumns = (quotes, columns) => {
  if (quotes.length === 0) return columns

  const [first, ...rest] = quotes
  const newColumns = columns.map((col, i) => (i === 0 ? [...col, first] : col))
  newColumns.sort((a, b) => calcHeight(a) - calcHeight(b))

  return calcColumns(rest, newColumns)
}

const measureQuoteHeight = (text, width) => {
  const div = document.createElement('div')

  div.style.position = 'absolute'
  div.style.left = '-1000px'
  div.style.top = '-1000px'
  div.style.width = `${width}px`
  div.style.fontSize = '14px'
  div.style.lineHeight = '16px'
  div.style.fontStyle = 'italic'
  div.style.overflow = 'hidden'
  div.style.overflowWrap = 'break-word'
  div.innerHTML = text || '&nbsp;'

  document.body.appendChild(div)
  const height = div.clientHeight + 48
  document.body.removeChild(div)

  return height
}

const OrderSummary = ({ order }) => {
  if (!order || !order.length) return null

  return (
    <div className={style['verbatim-quote-note']}>
      {order.map((item, i) => {
        const name = item.text || item.name || ''
        const qty = item.qty || item.quantity || 0
        const unit = qty === 1 ? 'pc' : 'pcs'

        // one order item per line — matches the platform Comments Page summary
        // name is encoded at rest; innerHTML decodes once (decodeText here would double-decode)
        return (
          <div key={`${name}-${i}`}>
            <span dangerouslySetInnerHTML={{ __html: name }} />
            {` – ${qty} ${unit}`}
          </div>
        )
      })}
    </div>
  )
}

OrderSummary.propTypes = {
  order: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      name: PropTypes.string,
      qty: PropTypes.number,
      quantity: PropTypes.number
    })
  )
}

const VerbatimQuote = ({ quote, onCopy }) => {
  const [copied, setCopied] = useState(false)

  const timerRef = useRef(null)

  const mountedRef = useRef(true)

  // Copied is shown only once the write has happened: a refused write that still said Copied
  // would leave the reader pasting whatever was on the clipboard before.
  const handleCopy = useCallback(async () => {
    if (!(await onCopy(quote.index)) || !mountedRef.current) return

    setCopied(true)

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = setTimeout(() => {
      setCopied(false)
      timerRef.current = null
    }, 2000)
  }, [quote.index, onCopy])

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false

      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  return (
    <div id={`verbatim-quote-${quote.index}`} className={style['verbatim-quote']}>
      <OrderSummary order={quote.order} />
      <div className={style['verbatim-quote-text']} dangerouslySetInnerHTML={{ __html: quote.quote }} />
      {quote.audio && (
        <div className={style['verbatim-quote-download']}>
          <a
            className={style['verbatim-quote-copy-text']}
            href={quote.audio.url}
            target="_blank"
            rel="noreferrer"
            download
          >
            Download audio
          </a>
          <i className="icon-aytmfd-microphone" />
        </div>
      )}
      <div
        className={`${style['verbatim-quote-copy']}${copied ? ' ' + style['verbatim-quote-copied'] : ''}`}
        aria-label={copied ? 'Copied' : 'Copy quote'}
        {...pressable(handleCopy)}
      >
        <span className={style['verbatim-quote-copy-text']}>Copy</span>
        <span className={style['verbatim-quote-copied-text']}>Copied!</span>
        <i className="icon-aytmfd-copy" />
      </div>
    </div>
  )
}

VerbatimQuote.propTypes = {
  quote: PropTypes.shape({
    index: PropTypes.number.isRequired,
    quote: PropTypes.string.isRequired,
    order: PropTypes.array,
    audio: PropTypes.shape({
      url: PropTypes.string
    })
  }).isRequired,
  onCopy: PropTypes.func.isRequired
}

const Verbatim = ({ answer, config }) => {
  const verbatim = answer?.verbatim || []
  const preset = config?.preset || 'median'

  // the quotes are measured against the room the list actually has, so the list measures its own
  // box rather than being told the report's
  const { componentRef, width } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH })

  const columns = useMemo(() => {
    if (!verbatim.length || !width) return []

    const quotesNumber = verbatim.length
    const columnsNumber = Math.min(3, quotesNumber)

    const w = width - COLUMN_GAP * (columnsNumber - 1) - 24
    const quoteWidth = (w - 2 * COLUMN_PADDING * columnsNumber) / columnsNumber

    const quotes = verbatim
      .map((quote, index) => {
        if (!quote) return null

        const quoteText = `&quot;${Object.hasOwn(quote, 'text') ? quote.text : quote}&quot;`
        const height = measureQuoteHeight(quoteText, quoteWidth)

        return {
          index,
          quote: quoteText,
          height,
          audio: quote.audio,
          order: quote.order
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.quote.length - b.quote.length)

    let resultColumns
    if (3 < quotes.length) {
      const emptyColumns = Array.from({ length: columnsNumber }, () => [])
      resultColumns = calcColumns(quotes, emptyColumns)
    } else {
      resultColumns = quotes.map(quote => [quote])
    }

    resultColumns = resultColumns.map(column => ({
      items: column,
      height: calcHeight(column)
    }))

    resultColumns.sort((a, b) => b.height - a.height)

    const maxColumnHeight = Math.max(...resultColumns.map(column => column.height))

    resultColumns = resultColumns.reduce((acc, cur, i) => {
      const rest = 100 - acc.reduce((sum, v) => sum + v.width, 0)
      const d = (maxColumnHeight - cur.height) / maxColumnHeight
      const width = columnsNumber - i > 1 ? (rest * (1 - d / 3)) / (columnsNumber - i) : rest

      acc.push({ items: cur.items, width })
      return acc
    }, [])

    if (Object.hasOwn(presetSortFunctions, preset)) {
      resultColumns.forEach(column => column.items.sort(presetSortFunctions[preset].items))
      resultColumns.sort(presetSortFunctions[preset].columns)
    }

    return resultColumns
  }, [width, verbatim, preset])

  const handleCopy = useCallback(index => copyToClipboard(decodeText(verbatim[index].text)), [verbatim])

  return (
    <div ref={componentRef} className={style['verbatim-view']}>
      {0 < columns.length ? (
        columns.map((column, columnIndex) => (
          <div key={columnIndex} className={style['verbatim-column']} style={{ width: `${column.width}%` }}>
            {column.items.map(item => (
              <VerbatimQuote key={item.index} quote={item} onCopy={handleCopy} />
            ))}
          </div>
        ))
      ) : (
        <div className={style['verbatim-empty']}>nothing found for your search criteria</div>
      )}
    </div>
  )
}

Verbatim.propTypes = {
  answer: PropTypes.shape({
    verbatim: PropTypes.array
  }),
  config: PropTypes.shape({
    preset: PropTypes.oneOf(['longest', 'median', 'shortest'])
  })
}

Verbatim.displayName = 'Verbatim'

// The quote surface every open-ended type shares. The measuring pass below is unconditional
// hooks, so whether the view is on screen at all -- the component flag and the in-flight
// payload sentinel, both readable off the props it already carries -- is decided out here.
const VerbatimView = ({ components, ...props }) => {
  if (!isComponentOn(components, 'verbatim')) return null
  if ('loading' === props.answer) return <ChartPlaceholder height={VERBATIM_PLACEHOLDER_HEIGHT} isLoading />

  return <Verbatim {...props} />
}

VerbatimView.displayName = 'VerbatimView'

VerbatimView.propTypes = {
  components: PropTypes.object,
  answer: answerPropType
}

export default VerbatimView
